import { Button, Container, Grid, Typography, useTheme } from "@mui/material";

import logo from "../icons/logo_color.svg";
import { useCallback, useEffect, useState } from "react";
import { Form } from "react-final-form";
import { useIntl } from "react-intl";
import { TextField } from "../components/form/TextField";
import { StyledForm } from "./LoginPage";
import { useApi } from "../utils/ApiProvider";
import { useHistory, useParams } from "react-router-dom";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";
import { snackbarOptions } from "../components/snackbar";
import { useLocale } from "../locales/LocaleProvider";
import { APP_NAME } from "../App";
import { useErrorHandler } from "../hooks/useErrorHandler";

type Passwords = {
  password: string;
  passwordAgain: string;
};

type CreateReset = {
  name: string;
};

export const PasswordResetPage = () => {
  const [valid, setValid] = useState<boolean | null>(null);
  const [notMatch, setNotMatch] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const theme = useTheme();
  const { formatMessage } = useIntl();
  const api = useApi();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { locale } = useLocale();

  // check password reset id
  const checkQuery = useCallback(() => api<{ valid: boolean }>(`/password-reset/${id}`), [api, id]);
  const onCheckSuccess = useCallback((response: { valid: boolean }) => setValid(response.valid), []);
  const onCheckError = useCallback(() => setValid(false), []);
  const { mutate: checkResetToken } = useMutation({ mutationFn: checkQuery, onSuccess: onCheckSuccess, onError: onCheckError });

  // set password
  const setPasswordQuery = useCallback(
    (password: string) =>
      api<any>(`/password-reset/${id}`, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      }),
    [api, id]
  );
  const onSetError = useCallback((res) => {
    setErrorMessage(res.message);
  }, []);
  const onSetSuccess = useCallback(() => {
    enqueueSnackbar(formatMessage({ defaultMessage: "Password updated." }), snackbarOptions({ variant: "success" }, { closeSnackbar }));
    history.push("/login");
  }, [closeSnackbar, enqueueSnackbar, formatMessage, history]);
  const { mutate: setPassword } = useMutation({ mutationFn: setPasswordQuery, onError: onSetError, onSuccess: onSetSuccess });

  const onSetSubmit = useCallback(
    async (args: Passwords) => {
      setErrorMessage(null);
      const { password, passwordAgain } = args;
      setNotMatch(password !== passwordAgain);
      if (password !== passwordAgain) {
        setErrorMessage(formatMessage({ defaultMessage: "Passwords are different." }));
        return;
      }
      setPassword(password);
    },
    [formatMessage, setPassword]
  );

  // send password reset e-mail
  const { errorHandler: onError } = useErrorHandler([]);
  const sendEmailQuery = useCallback(
    (name: string) =>
      api<void>(`/password-reset`, { method: "post", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, lang: locale }) }),
    [api, locale]
  );
  const { mutate: sendEmail, isSuccess: sendEmailSuccess, isError: sendEmailFailed } = useMutation({ mutationFn: sendEmailQuery, onError });

  const onCreateSubmit = useCallback(
    (args: CreateReset) => {
      const { name } = args;
      sendEmail(name);
    },
    [sendEmail]
  );

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "Change password" })} - ${APP_NAME}`;
  }, [formatMessage]);

  useEffect(() => {
    if (id === undefined) {
      return;
    }
    checkResetToken();
  }, [checkResetToken, id]);

  return (
    <Grid
      container
      direction="column"
      alignContent="center"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: "100vh",
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          backgroundColor: (theme) => theme.palette.common.white,
          padding: "5%",
          position: "relative",
          [theme.breakpoints.up("md")]: {
            borderRadius: "20px",
          },
        }}
      >
        <Grid container direction="column" alignContent="center" alignItems="center">
          <img src={logo} alt="" width="73px" height="73px"></img>
          <Typography
            sx={{
              fontWeight: "bold",
              "& > span": {
                color: theme.palette.secondary.main,
                textTransform: "none",
              },
              [theme.breakpoints.down("sm")]: {
                fontSize: "1.5rem",
                marginBottom: "20px",
              },
              "@media (max-width:350px)": {
                display: "none",
              },
            }}
            variant="h1"
          >
            <span>MOHAnet</span> Toolbox
          </Typography>
          {id === undefined ? (
            <>
              <Typography sx={{ marginBottom: "1rem" }} variant="subtitle1">
                {formatMessage({ defaultMessage: "To reset your password, enter your username." })}
              </Typography>
              <Form<CreateReset>
                onSubmit={onCreateSubmit}
                render={({ handleSubmit }) => (
                  <StyledForm onSubmit={handleSubmit}>
                    <Grid container spacing={1} sx={{ m: 1, flexDirection: "column" }}>
                      <Grid item xs={12}>
                        <TextField
                          name="name"
                          required
                          label={formatMessage({
                            defaultMessage: "Username",
                            description: "LoginPage username",
                          })}
                          variant="filled"
                          fullWidth
                          sx={{ m: 0 }}
                          InputLabelProps={{ shrink: undefined }}
                          highlightDirty={false}
                        />
                      </Grid>
                      {sendEmailSuccess && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="success.main">
                            {formatMessage({ defaultMessage: "Check your e-mails for your password-reset link." })}
                          </Typography>
                        </Grid>
                      )}
                      {sendEmailFailed && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="error.main">
                            {formatMessage({ defaultMessage: "Sending e-mail has failed. Please contact our support at support@mohanet.com." })}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                        <Button
                          sx={{
                            marginTop: "4rem",
                            paddingRight: "4.5rem",
                            paddingLeft: "4.5rem",
                            fontWeight: (theme) => theme.typography.fontWeightLight,
                          }}
                          variant="contained"
                          size="large"
                          type="submit"
                          disabled={sendEmailSuccess}
                        >
                          {formatMessage({ defaultMessage: "Send e-mail" })}
                        </Button>
                      </Grid>
                    </Grid>
                  </StyledForm>
                )}
              />
            </>
          ) : (
            <>
              <Typography sx={{ marginBottom: "1rem" }} variant="subtitle1">
                {formatMessage({ defaultMessage: "Reset password" })}
              </Typography>
              {valid === false && (
                <>
                  <Typography color="error">{formatMessage({ defaultMessage: "Reset password code does not exists, expired or used." })}</Typography>
                  <Button
                    sx={{
                      marginTop: "4rem",
                      paddingRight: "4.5rem",
                      paddingLeft: "4.5rem",
                      fontWeight: (theme) => theme.typography.fontWeightLight,
                    }}
                    variant="contained"
                    size="large"
                  >
                    {formatMessage({ defaultMessage: "Get new code" })}
                  </Button>
                </>
              )}
              {valid && (
                <Form<Passwords>
                  onSubmit={onSetSubmit}
                  autoComplete="new-password"
                  render={({ handleSubmit }) => (
                    <StyledForm onSubmit={handleSubmit}>
                      <Grid container spacing={1} sx={{ m: 1, flexDirection: "column" }}>
                        <Grid item xs={12}>
                          <TextField
                            name="password"
                            type="password"
                            required
                            label={formatMessage({ defaultMessage: "Password" })}
                            variant="filled"
                            fullWidth
                            sx={{ m: 0 }}
                            InputProps={{ error: notMatch }}
                            InputLabelProps={{ shrink: undefined }}
                            highlightDirty={false}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="passwordAgain"
                            type="password"
                            required
                            label={formatMessage({ defaultMessage: "Password again" })}
                            variant="filled"
                            fullWidth
                            sx={{ m: 0 }}
                            InputProps={{ error: notMatch }}
                            InputLabelProps={{ shrink: undefined }}
                            highlightDirty={false}
                          />
                        </Grid>
                        {errorMessage && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="error">
                              {errorMessage}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                          <Button
                            sx={{
                              marginTop: "4rem",
                              paddingRight: "4.5rem",
                              paddingLeft: "4.5rem",
                              fontWeight: (theme) => theme.typography.fontWeightLight,
                            }}
                            variant="contained"
                            size="large"
                            type="submit"
                          >
                            {formatMessage({ defaultMessage: "Set new password" })}
                          </Button>
                        </Grid>
                      </Grid>
                    </StyledForm>
                  )}
                />
              )}
            </>
          )}
        </Grid>
      </Container>
    </Grid>
  );
};
